<template>
  <div class="layout">
    <aside class="sidebar">
      <div class="brand">SSY Points Mall</div>
      <el-menu
        :default-active="$route.path"
        background-color="#1f2937"
        text-color="#d1d5db"
        active-text-color="#ffffff"
        router
      >
        <el-menu-item index="/home">首页</el-menu-item>
        <template v-for="menu in menuTree">
          <menu-node :key="menu.menuId" :menu="menu" />
        </template>
      </el-menu>
    </aside>

    <div class="content">
      <header class="header">
        <div>积分商城后台管理系统</div>
        <div class="header-actions">
          <el-popover
            placement="bottom-end"
            width="360"
            trigger="click"
            popper-class="notify-popover"
            @show="loadNotifyMessages"
          >
            <div class="notify-panel">
              <div class="notify-panel__head">
                <strong>待办消息</strong>
                <el-button v-if="notifyUnreadCount > 0" type="text" size="mini" @click="markAllNotifyRead">全部已读</el-button>
              </div>
              <div v-if="notifyMessages.length" class="notify-list">
                <div
                  v-for="item in notifyMessages"
                  :key="item.id"
                  class="notify-item"
                  :class="{ 'notify-item--unread': Number(item.readStatus) === 0 }"
                  @click="handleNotifyClick(item)"
                >
                  <div class="notify-item__title">
                    <span>{{ item.title }}</span>
                    <em v-if="Number(item.readStatus) === 0"></em>
                  </div>
                  <div class="notify-item__content">{{ item.content }}</div>
                  <div class="notify-item__time">{{ item.createTime }}</div>
                </div>
              </div>
              <div v-else class="notify-empty">暂无待办消息</div>
            </div>
            <el-badge slot="reference" :value="notifyUnreadCount" :max="99" :hidden="notifyUnreadCount <= 0">
              <button class="notify-button">
                <i class="el-icon-bell"></i>
                <span>待办</span>
              </button>
            </el-badge>
          </el-popover>

          <el-dropdown trigger="click" @command="handleUserCommand">
            <span class="user-dropdown">
              {{ currentUsername || '当前用户' }}<i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="resetPassword">重置密码</el-dropdown-item>
              <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>

      <div class="tabs-wrapper">
        <el-tabs
          :value="$route.path"
          type="card"
          closable
          @tab-click="handleTabClick"
          @tab-remove="removeTab"
        >
          <el-tab-pane
            v-for="tab in visitedTabs"
            :key="tab.path"
            :name="tab.path"
            :closable="tab.path !== '/home'"
          >
            <span slot="label">{{ tab.title }}</span>
          </el-tab-pane>
        </el-tabs>
      </div>

      <main class="page">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import http from '../utils/http'
import { clearPermissions, setPermissions } from '../store/permission'

const NOTIFY_POLL_INTERVAL = 180000

const MenuNode = {
  name: 'MenuNode',
  props: {
    menu: {
      type: Object,
      required: true
    }
  },
  methods: {
    resolveIndex (url) {
      if (!url) {
        return ''
      }
      return url.startsWith('/') ? url : `/${url}`
    }
  },
  render (h) {
    const menu = this.menu
    const children = Array.isArray(menu.children) ? menu.children.filter(item => item) : []
    if (children.length) {
      return h(
        'el-submenu',
        {
          props: {
            index: `menu-${menu.menuId}`
          }
        },
        [
          h('template', { slot: 'title' }, [menu.name]),
          ...children.map(child => h('menu-node', {
            key: child.menuId,
            props: { menu: child }
          }))
        ]
      )
    }

    if (menu.url) {
      return h(
        'el-menu-item',
        {
          props: {
            index: this.resolveIndex(menu.url)
          }
        },
        [menu.name]
      )
    }

    return null
  }
}

export default {
  components: {
    MenuNode
  },
  data () {
    return {
      menuTree: [],
      visitedTabs: [],
      currentUsername: localStorage.getItem('username') || '',
      notifyUnreadCount: 0,
      notifyMessages: [],
      notifyTimer: null
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler (route) {
        if (route.path && route.path !== '/login') {
          this.addTab(route)
        }
      }
    }
  },
  async created () {
    const token = localStorage.getItem('token')
    if (!token) {
      clearPermissions()
      this.$router.replace('/login')
      return
    }
    try {
      const { data } = await http.get('/sys/menu/nav')
      if (data.code !== 0) {
        this.logout()
        return
      }
      setPermissions(data.data.permissions || [])
      this.menuTree = this.buildTree(data.data.menuList || [])
      this.addTab(this.$route)
      this.loadCurrentUser()
      this.loadNotifyMessages()
      this.startNotifyPolling()
    } catch (e) {
      this.logout()
    }
  },
  beforeDestroy () {
    this.stopNotifyPolling()
  },
  methods: {
    async loadCurrentUser () {
      const { data } = await http.get('/sys/user/current')
      if (data.code === 0 && data.data) {
        this.currentUsername = data.data.username || ''
        localStorage.setItem('username', this.currentUsername)
      }
    },
    async loadNotifyMessages () {
      if (!localStorage.getItem('token')) {
        this.stopNotifyPolling()
        return
      }
      try {
        const { data } = await http.get('/mall/notify/admin/list', {
          params: { page: 1, limit: 8 }
        })
        if (data.code === 0 && data.data) {
          this.notifyMessages = data.data.list || []
          this.notifyUnreadCount = Number(data.data.unreadCount || 0)
          return
        }
        if (data.msg && data.msg.indexOf('登录已失效') > -1) {
          this.logout()
        }
      } catch (error) {
        // 通知轮询失败不影响后台主流程，token失效会由全局接口提示或下一次操作处理。
      }
    },
    startNotifyPolling () {
      this.stopNotifyPolling()
      this.notifyTimer = window.setInterval(() => {
        this.loadNotifyMessages()
      }, NOTIFY_POLL_INTERVAL)
    },
    stopNotifyPolling () {
      if (this.notifyTimer) {
        window.clearInterval(this.notifyTimer)
        this.notifyTimer = null
      }
    },
    async handleNotifyClick (item) {
      if (!item || !item.id) {
        return
      }
      if (Number(item.readStatus) === 0) {
        const { data } = await http.post(`/mall/notify/admin/read/${item.id}`)
        if (data.code === 0) {
          this.notifyUnreadCount = Math.max(this.notifyUnreadCount - 1, 0)
          item.readStatus = 1
        }
      }
      if (item.redirectPath) {
        this.$router.push(item.redirectPath).catch(() => {})
      }
      this.loadNotifyMessages()
    },
    async markAllNotifyRead () {
      const { data } = await http.post('/mall/notify/admin/read-all')
      if (data.code === 0) {
        this.notifyUnreadCount = 0
        this.notifyMessages = this.notifyMessages.map(item => ({ ...item, readStatus: 1 }))
      }
    },
    buildTree (list) {
      const menuList = (list || []).filter(item => Number(item.type) !== 2)
      const map = {}
      const roots = []
      menuList.forEach(item => {
        map[item.menuId] = { ...item, children: [] }
      })
      Object.values(map).forEach(item => {
        if (item.parentId && map[item.parentId]) {
          map[item.parentId].children.push(item)
        } else {
          roots.push(item)
        }
      })
      return roots
    },
    addTab (route) {
      if (!route || !route.path || route.path === '/login') {
        return
      }
      const exists = this.visitedTabs.some(tab => tab.path === route.path)
      if (exists) {
        return
      }
      this.visitedTabs.push({
        path: route.path,
        title: this.resolveTitle(route)
      })
    },
    resolveTitle (route) {
      if (route.meta && route.meta.title) {
        return route.meta.title
      }
      const matchedMenu = this.findMenuByPath(route.path.replace(/^\//, ''), this.menuTree)
      return matchedMenu ? matchedMenu.name : route.path
    },
    findMenuByPath (path, menus) {
      for (const menu of menus) {
        if (menu.url === path) {
          return menu
        }
        if (menu.children && menu.children.length) {
          const child = this.findMenuByPath(path, menu.children)
          if (child) {
            return child
          }
        }
      }
      return null
    },
    handleTabClick (tab) {
      if (tab.name && tab.name !== this.$route.path) {
        this.$router.push(tab.name)
      }
    },
    removeTab (targetPath) {
      if (targetPath === '/home') {
        return
      }
      const tabs = this.visitedTabs
      let nextPath = this.$route.path
      if (this.$route.path === targetPath) {
        const currentIndex = tabs.findIndex(tab => tab.path === targetPath)
        const nextTab = tabs[currentIndex + 1] || tabs[currentIndex - 1] || { path: '/home' }
        nextPath = nextTab.path
      }
      this.visitedTabs = tabs.filter(tab => tab.path !== targetPath)
      if (!this.visitedTabs.some(tab => tab.path === '/home')) {
        this.visitedTabs.unshift({ path: '/home', title: '首页' })
      }
      if (nextPath !== this.$route.path) {
        this.$router.push(nextPath)
      }
    },
    handleUserCommand (command) {
      if (command === 'resetPassword') {
        this.resetPassword()
        return
      }
      if (command === 'logout') {
        this.logout()
      }
    },
    resetPassword () {
      this.$confirm('确定重置当前账号密码吗？新密码会发送到你的邮箱，重置后需要重新登录。', '提示', { type: 'warning' }).then(async () => {
        const { data } = await http.post('/sys/user/reset-password')
        if (data.code === 0) {
          this.$message.success('随机密码已发送至邮箱，请使用新密码重新登录')
          this.logout()
        }
      })
    },
    logout () {
      this.stopNotifyPolling()
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      clearPermissions()
      this.visitedTabs = []
      this.$router.replace('/login')
    }
  }
}
</script>

<style scoped>
.layout {
  min-height: 100vh;
  display: flex;
  background: #f3f4f6;
}

.sidebar {
  width: 220px;
  background: #1f2937;
  color: #fff;
}

.brand {
  padding: 20px 16px;
  font-size: 20px;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.header {
  height: 60px;
  padding: 0 24px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 18px;
}

.notify-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: #eff6ff;
  color: #2563eb;
  cursor: pointer;
  font-size: 13px;
}

.notify-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid #eef2f7;
}

.notify-list {
  max-height: 360px;
  overflow-y: auto;
}

.notify-item {
  padding: 12px 2px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
}

.notify-item:hover {
  background: #f8fafc;
}

.notify-item__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #1f2937;
  font-weight: 600;
}

.notify-item__title em {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
}

.notify-item__content {
  margin-top: 6px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}

.notify-item__time {
  margin-top: 6px;
  color: #94a3b8;
  font-size: 12px;
}

.notify-item--unread .notify-item__title {
  color: #0f5ec9;
}

.notify-empty {
  padding: 24px 0 12px;
  color: #94a3b8;
  text-align: center;
}

.user-dropdown {
  color: #2563eb;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.tabs-wrapper {
  padding: 12px 16px 0;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.tabs-wrapper ::v-deep .el-tabs__header {
  margin: 0;
}

.tabs-wrapper ::v-deep .el-tabs__nav-wrap::after {
  height: 0;
}

.tabs-wrapper ::v-deep .el-tabs__item {
  height: 34px;
  line-height: 34px;
}

.page {
  padding: 24px;
}
</style>
